import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Loader2, Check, Sparkles, X, ArrowRight } from "lucide-react";
import axios from "axios";
import { useMarketConfig } from "@/utils/marketConfig";
import { companyOptions, joiningOptions } from "@/data/caraQuestions";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARA_AVATAR = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// ── Full-screen analysis overlay ────────────────────────────────────────────
// Takes over the screen while the engine runs: the signature score ring fills
// with progress around a masked "?", a dashed scanner ring sweeps slowly, and
// one stage at a time slides through the line below — spinner becomes a tick,
// the line lifts away, the next enters. `tick` = { idx, phase }.
function AnalysisOverlay({ stages, tick }) {
  const { idx, phase } = tick;
  const stageDone = phase === "done" || phase === "exit";
  const completed = Math.min(idx + (stageDone ? 1 : 0), stages.length);
  const progress = Math.min(100, ((idx + (stageDone ? 1 : 0.5)) / stages.length) * 100);
  const R = 72, CIRC = 2 * Math.PI * R;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/[0.97] backdrop-blur-sm overlay-fade flex flex-col items-center justify-center px-6" data-testid="analysis-overlay">
      {/* ambient glows — same fire-gradient language as the brand */}
      <div className="absolute top-1/4 -left-24 w-96 h-96 rounded-full bg-[#6B5CE6]/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-24 w-96 h-96 rounded-full bg-[#F0734E]/15 blur-3xl pointer-events-none" />

      {/* score ring — fills as stages complete, "?" stays masked until the report */}
      <div className="relative w-44 h-44 sm:w-52 sm:h-52 mb-10">
        <svg viewBox="0 0 176 176" className="absolute inset-0 w-full h-full spin-slow opacity-40" aria-hidden="true">
          <circle cx="88" cy="88" r="84" fill="none" stroke="#F0734E" strokeWidth="1.5" strokeDasharray="3 11" />
        </svg>
        <svg viewBox="0 0 176 176" className="absolute inset-0 w-full h-full -rotate-90">
          <defs>
            <linearGradient id="analysisGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6B5CE6" />
              <stop offset="50%" stopColor="#C75C8E" />
              <stop offset="100%" stopColor="#F0734E" />
            </linearGradient>
          </defs>
          <circle cx="88" cy="88" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
          <circle cx="88" cy="88" r={R} fill="none" stroke="url(#analysisGrad)" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={CIRC} strokeDashoffset={CIRC - (progress / 100) * CIRC}
            style={{ transition: "stroke-dashoffset 700ms cubic-bezier(0.22, 1, 0.36, 1)" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-['Plus_Jakarta_Sans'] text-5xl sm:text-6xl font-extrabold text-white">?</span>
          <span className="text-[10px] text-white/40 uppercase tracking-[0.25em] mt-1">/100</span>
        </div>
      </div>

      {/* one-line stage window — entering/leaving lines cross-fade through it */}
      <div className="relative h-8 w-full max-w-sm overflow-hidden" data-testid="analysis-stages">
        {stages.map((s, i) => {
          const isDone = i < idx || (i === idx && stageDone);
          let motion;
          if (i < idx || (i === idx && phase === "exit")) motion = "opacity-0 -translate-y-7"; // lifted away
          else if (i === idx && phase !== "enter") motion = "opacity-100 translate-y-0";       // on stage
          else motion = "opacity-0 translate-y-7";                                             // waiting below
          return (
            <div key={s} className={`absolute inset-0 flex items-center justify-center gap-3 transition-all duration-300 ease-out ${motion}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200
                ${isDone ? "bg-[#16A34A] text-white" : "bg-white/10 text-[#F0734E]"}`}>
                {isDone
                  ? <Check className="w-3.5 h-3.5 tick-pop" strokeWidth={3} />
                  : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </span>
              <span className="text-base font-medium text-white truncate">{s}</span>
            </div>
          );
        })}
      </div>

      <span className="font-['Plus_Jakarta_Sans'] text-xs text-white/40 mt-4 tabular-nums">{completed}/{stages.length}</span>
      <p className="text-white/30 text-xs mt-8 text-center">Deterministic engine · 10 recruiter dimensions · zero guesswork</p>
    </div>
  );
}

// NOTE: experience / skill_set / target_field were removed — those profile facts
// are now captured up front on the intake "calibrate your profile" step (level,
// category, skills). Cara only asks intent/context that the scorer uses to tune
// emphasis, plus an engagement question.
// Market-aware: company + notice-period options localize (India byte-identical;
// see data/caraQuestions.js — labels must keep resolve_company_key's keywords).
const buildQuestions = (market) => [
  {
    id: "target_company",
    text: "Where do you want to work next?",
    options: companyOptions(market)
  },
  {
    id: "career_goal",
    text: "What is the biggest goal for this resume?",
    options: [
      "Salary Hike",
      "Industry Change",
      "Role Promotion",
      "First Job"
    ]
  },
  {
    id: "joining_time",
    text: "How soon can you start the new job?",
    options: joiningOptions(market)
  },
  {
    id: "resume_worry",
    text: "What worries you most about your current resume?",
    options: [
      "Not getting shortlisted",
      "Low ATS score",
      "Weak work experience",
      "Not sure where to improve"
    ]
  }
];

// Auth gate shown at the analyze step. Signup-first (the visitor has just invested
// effort, so we lead with "create account"); on success it links the guest session
// to the new account and the caller runs the analysis.
function AuthGate({ onClose, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@") || password.length < 6) {
      toast.error("Enter a valid email and a password of at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") await register(email.trim(), password, name.trim());
      else await login(email.trim(), password);
      toast.success("You're in — analyzing your resume!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" data-testid="analyze-auth-gate">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ap-card max-w-md w-full p-8 animate-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--ap-muted)] hover:text-[var(--ap-ink)] transition-colors" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4 text-white" style={{ background: "var(--ap-fire)" }}>
            <Sparkles className="w-7 h-7" />
          </div>
          <h2 className="ap-display text-xl font-bold text-[var(--ap-ink)] mb-1.5">Your analysis is ready.</h2>
          <p className="text-[var(--ap-muted)] text-sm leading-relaxed">
            {mode === "signup"
              ? "Create your free account to unlock your ATS score and full diagnosis."
              : "Sign in to unlock your ATS score and full diagnosis."}
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white focus:outline-none focus:border-[var(--ap-violet)]" />
          )}
          <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white focus:outline-none focus:border-[var(--ap-violet)]" />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--ap-line)] text-sm text-[var(--ap-ink)] bg-white focus:outline-none focus:border-[var(--ap-violet)]" />
          <button type="submit" disabled={busy} className="ap-btn ap-btn-fire ap-btn-md w-full disabled:opacity-70" data-testid="analyze-auth-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === "signup" ? "Create account & see results" : "Sign in & see results"} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
        <button onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
          className="w-full text-[13px] text-[var(--ap-muted)] mt-3 hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer">
          {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
        </button>
        <p className="text-center text-[11px] text-[var(--ap-muted)] mt-3">Free ATS score · no card required</p>
      </div>
    </div>
  );
}

export default function ChatbotPage() {
  // Cara's options follow the visitor's market (India unchanged; US sees
  // "Big Tech (Google, Amazon, Microsoft)" not "Big MNCs (TCS, Infosys)").
  const { cfg: mkt } = useMarketConfig();
  const QUESTIONS = useMemo(() => buildQuestions(mkt.market), [mkt.market]);
  const [messages, setMessages] = useState([]);
  const [currentQ, setCurrentQ] = useState(-1);
  const [responses, setResponses] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStages, setAnalysisStages] = useState([]);
  const [tick, setTick] = useState({ idx: 0, phase: "enter" });
  const [initialized, setInitialized] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingAnalyze, setPendingAnalyze] = useState(false);
  const messagesEnd = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { getHeaders, user } = useAuth();

  const resumeId = location.state?.resumeId;
  const targetRole = location.state?.targetRole || user?.target_role || "";
  const profile = location.state?.profile || {};

  useEffect(() => {
    if (!resumeId) { navigate("/intake"); return; }
    if (initialized) return;
    setInitialized(true);

    const timer1 = setTimeout(() => {
      addBotMessage("I've scanned your resume. Before we generate the analysis, I need to understand few things.");
      const timer2 = setTimeout(() => askQuestion(0), 1500);
      return () => clearTimeout(timer2);
    }, 800);

    return () => clearTimeout(timer1);
  }, [resumeId]); // eslint-disable-line

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { type: "bot", text, id: Date.now() }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: "user", text, id: Date.now() }]);
  };

  const getQuestionText = (q) => {
    return q.text;
  };

  const askQuestion = (index) => {
    if (index >= QUESTIONS.length) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addBotMessage("Got it. Let's see what the ATS thinks of you.");
        setShowFinal(true);
      }, 1000);
      return;
    }
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setCurrentQ(index);
      addBotMessage(getQuestionText(QUESTIONS[index]));
    }, 1200);
  };

  const handleResponse = (questionId, answer) => {
    addUserMessage(answer);
    setResponses(prev => ({ ...prev, [questionId]: answer }));
    setCurrentQ(-1);
    setTimeout(() => {
      const idx = QUESTIONS.findIndex(q => q.id === questionId);
      askQuestion(idx + 1);
    }, 500);
  };

  // Dynamic stage labels — they reflect what the engine actually does with
  // this user's answers, so the theater is honest.
  const buildStages = () => {
    const c = (responses.target_company || "").toLowerCase();
    const fitLabel = c.includes("mnc") || c.includes("big") ? "Checking your Big-MNC fit"
      : c.includes("startup") ? "Checking your startup fit"
      : c.includes("remote") ? "Checking your remote-work fit"
      : c.includes("small") || c.includes("medium") ? "Checking your SMB fit"
      : "Checking target-company fit";
    return [
      "Parsing your resume",
      "Scanning 10 recruiter dimensions",
      `Matching ${targetRole || "your role"} keywords`,
      fitLabel,
      "Computing your honest score",
    ];
  };

  const runAnalyze = async () => {
    setShowFinal(false);
    setAnalyzing(true);
    const stages = buildStages();
    setAnalysisStages(stages);
    setTick({ idx: 0, phase: "enter" });

    // Fire the real request up front; the overlay animates while it runs and
    // the last stage's spinner holds until the engine actually answers.
    const chatbotResponses = Object.entries(responses).map(([qId, answer]) => ({
      question_id: qId,
      answer
    }));
    const apiPromise = axios.post(`${API}/analysis/trigger`, {
      resume_id: resumeId,
      target_role: targetRole,
      chatbot_responses: chatbotResponses,
      // Structured profile from the intake step → drives the level-aware scorer.
      years_experience: profile.years_experience ?? null,
      level: profile.level ?? null,
      role_category: profile.role_category ?? "",
      primary_skills: profile.primary_skills ?? [],
      current_title: profile.current_title ?? "",
    }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
    apiPromise.catch(() => {}); // rejection is handled where it's awaited below

    try {
      for (let i = 0; i < stages.length; i++) {
        setTick({ idx: i, phase: "enter" });
        await wait(60); // mount below the fold, then slide in
        setTick({ idx: i, phase: "active" });
        if (i === stages.length - 1) {
          await apiPromise; // hold "Computing your honest score" until it's real
          await wait(350);
        } else {
          await wait(800);
        }
        setTick({ idx: i, phase: "done" }); // spinner → tick pop
        await wait(450);
        if (i < stages.length - 1) {
          setTick({ idx: i, phase: "exit" }); // lift away, next slides in
          await wait(280);
        }
      }
      const res = await apiPromise;
      await wait(500); // let the final tick land, then straight to the report
      navigate(`/results?analysis_id=${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
      setAnalyzing(false);
      addBotMessage("Something went wrong with the analysis. Let's try again.");
      setShowFinal(true);
    }
  };

  // Login gate: the user has already invested effort (intake + Cara's questions)
  // by the time they hit "Analyze", so asking them to create a free account here
  // converts far better than a cold signup wall. A guest gets the auth modal; the
  // real analysis fires only once they're authenticated.
  const handleAnalyze = () => {
    if (!user) { setAuthOpen(true); return; }
    runAnalyze();
  };

  // Run the analysis on the render AFTER auth completes, so the request carries
  // the fresh token (getAuthHeader reads token from state, not localStorage).
  useEffect(() => {
    if (pendingAnalyze && user) { setPendingAnalyze(false); runAnalyze(); }
  }, [pendingAnalyze, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (messagesEnd.current) {
      messagesEnd.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, isTyping, currentQ, showFinal, tick]);

  const currentQuestion = currentQ >= 0 ? QUESTIONS[currentQ] : null;
  const answered = Object.keys(responses).length;

  return (
    <div className="ap-root min-h-screen pt-24 flex flex-col bg-[var(--ap-canvas)]" data-testid="chatbot-page">
      <div className="ap-mesh flex-1 flex flex-col max-w-2xl mx-auto w-full px-4">
        {/* Header — Cara avatar with live pulse + calibration progress */}
        <div className="sticky top-16 z-10 ap-glass rounded-3xl overflow-hidden">
          <div className="px-5 py-3.5 flex items-center gap-3">
            <div className="relative shrink-0">
              <img src={CARA_AVATAR} alt="Cara" className="w-11 h-11 rounded-full object-cover ring-2 ring-[var(--ap-violet)]/25" />
              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[var(--ap-green)] border-2 border-white">
                <span className="absolute inset-0 rounded-full bg-[var(--ap-green)] animate-ping opacity-50" />
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="ap-display text-[var(--ap-ink)] text-[15px] font-bold truncate flex items-center gap-1.5" data-testid="recruiter-name">
                Cara — Your Career Companion
              </h2>
              <span className="text-[var(--ap-muted)] text-xs flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-[var(--ap-violet)]" />
                {showFinal ? "Calibration complete — ready to analyze" : "Calibrating your analysis"}
              </span>
            </div>
            <span className="font-['Plus_Jakarta_Sans'] text-xs font-bold text-[var(--ap-muted)] shrink-0 tabular-nums">{answered}/{QUESTIONS.length}</span>
          </div>
          {/* calibration progress */}
          <div className="h-1 bg-[var(--ap-canvas-2)]">
            <div
              className="h-full transition-all duration-500 ease-out"
              style={{ background: "var(--ap-fire)", width: `${(answered / QUESTIONS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto py-8" data-testid="chat-messages">
          {messages.map((msg, mi) => {
            // Consecutive same-sender messages form a group: avatar on the
            // first only, tight gap inside the group, breathing room between.
            const firstOfGroup = mi === 0 || messages[mi - 1].type !== msg.type;
            return (
            <div key={msg.id} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"} animate-fade-in-up ${mi === 0 ? "" : firstOfGroup ? "mt-6" : "mt-1.5"}`}>
              {msg.type === "bot" && (
                firstOfGroup
                  ? <img src={CARA_AVATAR} alt="" className="w-9 h-9 rounded-full ring-2 ring-[var(--ap-violet)]/15 mr-3 mt-0.5 shrink-0 object-cover" />
                  : <span className="w-9 mr-3 shrink-0" aria-hidden="true" />
              )}
              <div className={msg.type === "bot"
                ? `bg-[var(--ap-card)] border border-[var(--ap-line)] text-[var(--ap-ink)] rounded-2xl ${firstOfGroup ? "rounded-tl-md" : ""} px-4 py-3 max-w-[80%] shadow-[var(--ap-shadow-sm)]`
                : `text-white rounded-2xl ${firstOfGroup ? "rounded-tr-md" : ""} px-4 py-3 max-w-[80%] shadow-[0_12px_30px_-12px_rgba(107,92,230,0.5)]`
              } style={msg.type === "user" ? { background: "var(--ap-fire)" } : undefined}>
                <p className="text-sm leading-relaxed">{msg.text}</p>
              </div>
            </div>
            );
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex items-start mt-6 animate-fade-in-up">
              <img src={CARA_AVATAR} alt="" className="w-9 h-9 rounded-full ring-2 ring-[var(--ap-violet)]/15 mr-3 mt-0.5 shrink-0 object-cover" />
              <div className="bg-[var(--ap-card)] border border-[var(--ap-line)] rounded-2xl rounded-tl-md px-4 py-[15px] shadow-[var(--ap-shadow-sm)] flex gap-1.5">
                <span className="typing-dot w-2 h-2 bg-[var(--ap-violet)] rounded-full" />
                <span className="typing-dot w-2 h-2 bg-[var(--ap-violet)] rounded-full" />
                <span className="typing-dot w-2 h-2 bg-[var(--ap-violet)] rounded-full" />
              </div>
            </div>
          )}

          {/* Answer pills — staggered entrance, fill on hover */}
          {currentQuestion && !isTyping && (
            <div className="flex flex-wrap gap-2.5 ml-12 mt-4" data-testid={`postback-group-${currentQuestion.id}`}>
              {currentQuestion.options.map((opt, oi) => (
                <button
                  key={opt}
                  onClick={() => handleResponse(currentQuestion.id, opt)}
                  className="animate-fade-in-up px-4 py-2.5 rounded-full border border-[var(--ap-violet)]/25 bg-[var(--ap-card)] text-[var(--ap-violet)] text-sm font-semibold hover:bg-[var(--ap-violet)] hover:text-white hover:border-[var(--ap-violet)] hover:shadow-[var(--ap-glow)] hover:-translate-y-0.5 transition-all cursor-pointer"
                  style={{ animationDelay: `${oi * 80}ms` }}
                  data-testid={`postback-${currentQuestion.id}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}

          {/* Final CTA */}
          {showFinal && !analyzing && (
            <div className="flex justify-center mt-10 animate-fade-in-up">
              <button
                onClick={handleAnalyze}
                className="ap-btn ap-btn-fire ap-btn-lg"
                data-testid="analyze-cta-btn"
              >
                Analyze my resume <Send className="w-[18px] h-[18px]" />
              </button>
            </div>
          )}

          <div ref={messagesEnd} className="h-4" />
        </div>
      </div>

      {/* Login gate — the user invests effort first, then creates an account here */}
      {authOpen && (
        <AuthGate
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); setPendingAnalyze(true); }}
        />
      )}

      {/* Full-screen takeover while the engine runs */}
      {analyzing && <AnalysisOverlay stages={analysisStages} tick={tick} />}
    </div>
  );
}
